import {
  getDomainForLocale,
  getLocaleDomains,
  getLocaleForInternalLocaleOrigin,
  isLocaleDomainMapped,
  isMultiDomainEnabled,
  stripLocalePrefix,
} from "./localeDomains";

export const BASE_URL_API =
  process.env.WP_API_URL?.slice(-1) === "/"
    ? `${process.env.WP_API_URL.slice(0, -1)}`
    : process.env.WP_API_URL;

export const getWpJsonUrl = (lang?: string, defaultLang?: string) => {
  return lang && defaultLang && lang !== defaultLang
    ? `${BASE_URL_API}/${lang}/wp-json`
    : BASE_URL_V1;
};

export const getAundsV1Url = (lang?: string, defaultLang?: string) => {
  return `${getWpJsonUrl(lang, defaultLang)}/aunds/v1`;
};

export const getUrl = (lang?: string, defaultLang?: string, v1?: boolean) => {
  if (v1) {
    return getWpJsonUrl(lang, defaultLang);
  } else {
    return lang && defaultLang && lang !== defaultLang
      ? `${BASE_URL_API}/${lang}/wp-json/wp/v2`
      : BASE_URL;
  }
};

export const BASE_URL_V1 = `${BASE_URL_API}/wp-json`;
export const BASE_URL_V3 = `${BASE_URL_API}/wp-json/acf/v3`;
export const BASE_URL = `${BASE_URL_API}/wp-json/wp/v2`;

/**
 * Get relative path from link. Please pass also the type property for menu items to enable external links.
 * @param url
 * @param urlType (for menu links only)
 */
export const getRelativePathFromLink = (url: string, urlType = "default") => {
  if (urlType === "custom") return url;
  if (validURL(url)) {
    const u = new URL(url);
    return `${u.pathname}${u.search}${u.hash}`;
  }
  // WP occasionally returns absolute links without a scheme (e.g.
  // "example.com/path/" for post.link or the frontpage season page urls) -
  // retry as if https:// were prepended before falling back to treating it
  // as a relative path outright.
  // TODO: check returned link from wordpress
  if (!url.startsWith("/") && !url.startsWith("#")) {
    const withScheme = `https://${url}`;
    if (validURL(withScheme)) {
      const u = new URL(withScheme);
      return `${u.pathname}${u.search}${u.hash}`;
    }
  }
  return url;
};

interface getPathNameArrayParams {
  url: string;
  blacklist?: string[];
}
export const getPathNameArray = ({
  url,
  blacklist = [],
}: getPathNameArrayParams) => {
  const path = getRelativePathFromLink(url);
  return path.split("/").filter((el) => el !== "" && !blacklist.includes(el));
};

const defaultLocale = process.env.DEFAULT_LOCALE;
const trailingSlash = process.env.TRAILING_SLASH === "true";

const splitUrlParts = (url: string) => {
  const hashIndex = url.indexOf("#");
  const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const searchIndex = urlWithoutHash.indexOf("?");

  return {
    pathname:
      searchIndex === -1
        ? urlWithoutHash
        : urlWithoutHash.slice(0, searchIndex),
    search: searchIndex === -1 ? "" : urlWithoutHash.slice(searchIndex),
    hash,
  };
};

const applyTrailingSlashToPathname = (pathname: string) => {
  const cleaned = pathname.replace(/\/+$/, "");

  return trailingSlash
    ? cleaned === ""
      ? "/"
      : `${cleaned}/`
    : cleaned || "/";
};

export const optimizeInternalUrl = (href: string) => {
  const { search, hash, ...urlParts } = splitUrlParts(href);
  let { pathname } = urlParts;

  pathname =
    pathname.replace(new RegExp(`^/${defaultLocale}(?=/|$)`), "") || "/";
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;

  return `${applyTrailingSlashToPathname(pathname)}${search}${hash}`;
};

function validURL(str: string) {
  try {
    new URL(str);
    // eslint-disable-next-line  unused-imports/no-unused-vars
  } catch (e) {
    return false;
  }
  return true;
}

//TODO: maybe check this only once at startup
const checkPublicURLEnv = () => {
  if (!process.env.NEXT_PUBLIC_PUBLIC_URL) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL not set but indispensable for isInternalLinkAbsolute",
    );
  }
  if (!validURL(process.env.NEXT_PUBLIC_PUBLIC_URL)) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL " +
        process.env.NEXT_PUBLIC_PUBLIC_URL +
        " is no valid url",
    );
  }
};

// checks whether an absolute url points at a locale-specific domain
// configured via NEXT_PUBLIC_LOCALE_DOMAINS
export const isMappedLocaleDomainLink = (url: string) => {
  if (!validURL(url)) return false;
  const { host } = new URL(url);
  return Object.values(getLocaleDomains()).some(
    (origin) => new URL(origin).host === host,
  );
};

// checks whether an absolute url points at NEXT_PUBLIC_PUBLIC_URL's origin
const isPublicUrlLink = (url: string) =>
  validURL(url) &&
  !!process.env.NEXT_PUBLIC_PUBLIC_URL &&
  new URL(url).origin === new URL(process.env.NEXT_PUBLIC_PUBLIC_URL).origin;

//checks whether an link is internal and absolute
export const isInternalLinkAbsolute = (url: string) => {
  checkPublicURLEnv();
  return !!(
    validURL(url) &&
    (new URL(url).hostname ===
      new URL(process.env.NEXT_PUBLIC_PUBLIC_URL!).hostname ||
      isMappedLocaleDomainLink(url) ||
      getLocaleForInternalLocaleOrigin(url) ||
      (process.env.NEXT_PUBLIC_INTERNAL_ORIGINS &&
        process.env.NEXT_PUBLIC_INTERNAL_ORIGINS?.split(",")?.some?.(
          (internalHost) => {
            return (
              validURL(internalHost) &&
              new URL(url).hostname === new URL(internalHost).hostname
            );
          },
        )))
  );
};

/**
 * is intended to optimize all kinds of urls
 * currently does following:
 * - internal absolute urls are converted to relative ones
 * - trailing slash is applied or removed according to next.config.js to prevent 3xx errors
 *
 * @param url url to optimize
 * @returns optimized url
 */
export const optimizeURL = (url: string) => {
  if (!url) return;
  if (url === "/") return url;
  if (url.startsWith("/#")) {
    return url;
  }
  if (url.startsWith("#")) {
    return "/" + url;
  }
  if (isMappedLocaleDomainLink(url)) {
    // links to a locale-specific domain must stay absolute — relativizing
    // them would serve the wrong locale when rendered on another domain
    return url;
  }
  if (isMultiDomainEnabled() && isPublicUrlLink(url)) {
    // with multi-domain routing this deployment's own default locale domain
    // must stay absolute too — keeps already localized links (e.g. from
    // translateInternalLinks) pointing at the default locale on every domain
    return url;
  }
  const internalOriginLocale = getLocaleForInternalLocaleOrigin(url);
  if (internalOriginLocale) {
    // the locale lives in the link's host, not its path — restore the
    // "/{locale}" prefix so it maps onto this deployment's locale routing
    const relativeURL = getRelativePathFromLink(url);
    const localizedURL =
      internalOriginLocale === defaultLocale
        ? relativeURL
        : `/${internalOriginLocale}${relativeURL}`;
    return localizeInternalUrl(localizedURL);
  }
  if (isInternalLinkAbsolute(url)) {
    const relativeURL = getRelativePathFromLink(url);
    return localizeInternalUrl(relativeURL);
  }
  if (!validURL(url)) {
    //assume relative url
    return localizeInternalUrl(url);
  }
  return url;
};

/**
 * Optimizes a relative internal url and maps its locale prefix onto the
 * locale's domain. An explicit default locale prefix ("/de/foo") pins the
 * link to the default locale: with multi-domain routing it becomes absolute,
 * since a relative "/foo" would resolve to the locale of the domain it is
 * rendered on.
 */
const localizeInternalUrl = (relativeUrl: string) => {
  const { pathname, search, hash } = splitUrlParts(relativeUrl);
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  if (
    defaultLocale &&
    firstSegment === defaultLocale &&
    isMultiDomainEnabled()
  ) {
    return buildAbsoluteInternalLink(
      `${stripLocalePrefix(pathname, defaultLocale)}${search}${hash}`,
      defaultLocale,
    );
  }
  return mapLocalePrefixToDomain(optimizeInternalUrl(relativeUrl));
};

/**
 * Turns a relative url with a locale prefix of a domain-mapped locale
 * ("/en/foo") into an absolute link on that locale's domain — saves the
 * middleware redirect hop and is host-agnostic (correct on every domain).
 */
const mapLocalePrefixToDomain = (relativeUrl: string) => {
  const firstSegment = relativeUrl.split("/").filter(Boolean)[0];
  if (firstSegment && isLocaleDomainMapped(firstSegment)) {
    return `${getDomainForLocale(firstSegment)}${stripLocalePrefix(
      relativeUrl,
      firstSegment,
    )}`;
  }
  return relativeUrl;
};

export const setTrailingSlash = (url: string) => {
  const { pathname, search, hash } = splitUrlParts(url);

  return `${applyTrailingSlashToPathname(pathname)}${search}${hash}`;
};

/**
 * Builds an internal absolute link from a relative or absolute (independent which origin) url
 * @param url url (relative or absolute) to be transformed to an absolute internal link
 * @param locale when the locale has its own domain (NEXT_PUBLIC_LOCALE_DOMAINS),
 *               the link uses that domain and drops the locale path prefix
 */
export const buildAbsoluteInternalLink = (url: string, locale?: string) => {
  checkPublicURLEnv();
  const relativeURL = getRelativePathFromLink(url);
  if (!relativeURL.startsWith("/")) {
    throw new Error(
      "can't build absolute internal link from relative url which does not start with /",
    );
  }
  if (locale && isLocaleDomainMapped(locale)) {
    return setTrailingSlash(
      `${getDomainForLocale(locale)}${stripLocalePrefix(relativeURL, locale)}`,
    );
  }
  return setTrailingSlash(
    `${process.env.NEXT_PUBLIC_PUBLIC_URL}${relativeURL}`,
  );
};

// Transliteration map for characters that would otherwise be stripped when
// slugifying a label (primarily German umlauts and the sharp s).
const TRANSLITERATION_MAP: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  ß: "ss",
};

/**
 * Convert a human-readable label (e.g. a translated season name) into a URL
 * slug. Lowercases, transliterates German special characters, collapses
 * whitespace to hyphens and strips anything outside [a-z0-9-]. Returns null when
 * the result is not a valid WP slug, so callers can fall back gracefully.
 *
 * @example labelToSlug("Sommer") // "sommer"
 * @example labelToSlug("Winter") // "winter"
 */
export function labelToSlug(label: string): string | null {
  if (!label) return null;

  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[äöüß]/g, (char) => TRANSLITERATION_MAP[char] ?? char)
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return isValidWpSlug(slug) ? slug : null;
}

// Check if frontend slug is valid before passing it to the backend to prevent DOS attacks
export function isValidWpSlug(slug: string): boolean {
  // Slug must be a non-empty string, and only contain lowercase letters, numbers, and hyphens
  const wpSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  // Trim whitespace from the slug (although WP slugs shouldn't have leading/trailing spaces)
  const trimmedSlug = slug?.trim();

  // Return true if the slug matches the pattern
  return wpSlugPattern.test(trimmedSlug);
}
